import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker, useDayRender, type DayProps } from "react-day-picker";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  /**
   * Stamp a `data-testid` on a day button. Return undefined for days that
   * should not carry one.
   *
   * A day button's only text is the day number, which is what a recorded
   * browser test ends up identifying it by — and the day that is bookable
   * moves, so the recording breaks (EAGNT-744). The testid is the stable name
   * such a test can hold on to, so it has to be unique on the page.
   */
  dayTestId?: (date: Date) => string | undefined;
};

// Stable icon components — defined outside to avoid recreation
const IconLeft = () => <ChevronLeft className="h-4 w-4" />;
const IconRight = () => <ChevronRight className="h-4 w-4" />;
const NAV_COMPONENTS = { IconLeft, IconRight } as const;

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  locale,
  dayTestId,
  ...props
}: CalendarProps) {
  // react-day-picker has no per-day prop hook, so the day button is replaced
  // wholesale. `useDayRender` hands back everything the built-in Day renders
  // with, so this stays behaviourally identical apart from the attribute.
  const components = React.useMemo(() => {
    if (!dayTestId) return NAV_COMPONENTS;
    const Day = (dayProps: DayProps) => {
      const buttonRef = React.useRef<HTMLButtonElement>(null);
      const day = useDayRender(dayProps.date, dayProps.displayMonth, buttonRef);
      if (day.isHidden) return <div role="gridcell" />;
      if (!day.isButton) return <div {...day.divProps} />;
      return (
        <button
          type="button"
          ref={buttonRef}
          {...day.buttonProps}
          data-testid={dayTestId(dayProps.date)}
        />
      );
    };
    return { ...NAV_COMPONENTS, Day };
  }, [dayTestId]);

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3 pointer-events-auto", className)}
      locale={locale}
      {...props}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        caption: "flex justify-center pt-1 relative items-center",
        caption_label: "text-sm font-medium",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex",
        head_cell:
          "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
        row: "flex w-full mt-2",
        cell: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
        day: cn(
          buttonVariants({ variant: "ghost" }),
          "h-9 w-9 p-0 font-normal aria-selected:opacity-100 cursor-pointer hover:bg-accent hover:text-accent-foreground",
        ),
        day_range_end: "day-range-end",
        day_selected:
          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
        day_today: "bg-accent text-accent-foreground",
        day_outside:
          "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
        day_disabled: "text-muted-foreground opacity-50 pointer-events-none",
        day_range_middle:
          "aria-selected:bg-accent aria-selected:text-accent-foreground",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={components}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
